import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../utils/imageUrl';
import LanguageSwitcher from './common/LanguageSwitcher';
import './LandingPage.css';
import './business/BusinessPage.css';
import './SignupPage.css';
import './PartnersPage.css';

const PartnersPage = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const [logoUrl] = useState(null);

  const goToApply = () => navigate('/partners/apply');

  return (
    <div className="landing-page business-page">
      {/* Navigation Bar - same as Business page */}
      <nav className="landing-navbar">
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            {logoUrl ? (
              <img
                src={getImageUrl(logoUrl) || logoUrl}
                alt="Syllera"
                className="landing-logo-img"
              />
            ) : (
              <span className="material-icons">school</span>
            )}
            <span>Syllera</span>
          </div>
          <div className="nav-menu">
            <a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>{t('nav.features')}</a>
            <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>{t('nav.howItWorks')}</a>
            <a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>{t('nav.pricing')}</a>
            <a href="/#testimonials" onClick={(e) => { e.preventDefault(); navigate('/#testimonials'); }}>{t('nav.reviews')}</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); }}>{t('nav.resources')}</a>
            <a href="/business" onClick={(e) => { e.preventDefault(); navigate('/business'); }}>{t('nav.business')}</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => navigate('/#login')}>{t('nav.login')}</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>{t('nav.startLearning')}</button>
          </div>
        </div>
      </nav>

      {/* [Hero + Partner Types - Business page style: business_center + cards] */}
      <div className="business-container" style={{ paddingTop: '80px' }}>
        <div className="business-header">
          <h1 className="business-title">
            <span className="material-icons">business_center</span>
            {t('partners.heroTitle')}
          </h1>
          <p className="business-subtitle">
            {t('partners.heroSubtitle')}
          </p>
        </div>

        <h2 className="partners-who-title">{t('partners.whoTitle')}</h2>
        <div className="business-cards partners-cards-row">
          <div className="business-card teacher">
            <span className="material-icons business-card-icon">groups</span>
            <h3>{t('partners.teacher')}</h3>
            <p className="business-card-desc">{t('partners.teacherDesc')}</p>
            <button
              type="button"
              className="btn-primary partners-card-apply-btn"
              onClick={() => navigate('/partners/apply?type=teacher')}
            >
              <span className="material-icons">person_add</span>
              {t('partners.applyNow')}
            </button>
          </div>
          <div className="business-card school">
            <span className="material-icons business-card-icon">account_balance</span>
            <h3>{t('partners.school')}</h3>
            <p className="business-card-desc">{t('partners.schoolDesc')}</p>
            <button
              type="button"
              className="btn-primary partners-card-apply-btn"
              onClick={() => navigate('/partners/apply?type=school')}
            >
              <span className="material-icons">person_add</span>
              {t('partners.applyNow')}
            </button>
          </div>
          <div className="business-card influencer">
            <span className="material-icons business-card-icon">campaign</span>
            <h3>{t('partners.influencer')}</h3>
            <p className="business-card-desc">{t('partners.influencerDesc')}</p>
            <button
              type="button"
              className="btn-primary partners-card-apply-btn"
              onClick={() => navigate('/partners/apply?type=influencer')}
            >
              <span className="material-icons">person_add</span>
              {t('partners.applyNow')}
            </button>
          </div>
        </div>
      </div>

      {/* [Benefits of Partnership] */}
      <section className="partners-benefits-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('partners.benefitsTitle')}</h2>
            <p className="section-subtitle">{t('partners.benefitsSubtitle')}</p>
          </div>
          <div className="partners-benefits-grid">
            <div className="partners-benefit-card">
              <span className="material-icons partners-benefit-icon">paid</span>
              <h3>{t('partners.earnCommission')}</h3>
              <p>{t('partners.earnCommissionDesc')}</p>
            </div>
            <div className="partners-benefit-card">
              <span className="material-icons partners-benefit-icon">autorenew</span>
              <h3>{t('partners.longTermEarnings')}</h3>
              <p>{t('partners.longTermEarningsDesc')}</p>
            </div>
            <div className="partners-benefit-card">
              <span className="material-icons partners-benefit-icon">psychology</span>
              <h3>{t('partners.educationalImpact')}</h3>
              <p>{t('partners.educationalImpactDesc')}</p>
            </div>
            <div className="partners-benefit-card">
              <span className="material-icons partners-benefit-icon">insights</span>
              <h3>{t('partners.partnerDashboard')}</h3>
              <p>{t('partners.partnerDashboardDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* [Sample Scenario] */}
      <section className="partners-scenario-section">
        <div className="section-container" style={{ textAlign: 'center' }}>
          <h2>{t('partners.scenarioTitle')}</h2>
          <p className="partners-scenario-lead">
            {t('partners.scenarioLead')}
          </p>
          <p className="partners-scenario-note">
            {t('partners.scenarioNote')}
          </p>
        </div>
      </section>

      {/* [How It Works] - same layout as main landing (steps-container, step-item, step-number, step-icon, step-arrow) */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('partners.howItWorksTitle')}</h2>
            <p className="section-subtitle">{t('partners.howItWorksSubtitle')}</p>
          </div>
          <div className="steps-container">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-icon">
                <span className="material-icons">edit_note</span>
              </div>
              <h3 className="step-title">{t('partners.step1Title')}</h3>
              <p className="step-desc">{t('partners.step1Desc')}</p>
              <div className="step-arrow">
                <span className="material-icons">arrow_forward</span>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-icon">
                <span className="material-icons">verified_user</span>
              </div>
              <h3 className="step-title">{t('partners.step2Title')}</h3>
              <p className="step-desc">{t('partners.step2Desc')}</p>
              <div className="step-arrow">
                <span className="material-icons">arrow_forward</span>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-icon">
                <span className="material-icons">dashboard</span>
              </div>
              <h3 className="step-title">{t('partners.step3Title')}</h3>
              <p className="step-desc">{t('partners.step3Desc')}</p>
              <div className="step-arrow">
                <span className="material-icons">arrow_forward</span>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-icon">
                <span className="material-icons">link</span>
              </div>
              <h3 className="step-title">{t('partners.step4Title')}</h3>
              <p className="step-desc">{t('partners.step4Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA removed: each partner card has its own Apply button */}

      {/* Footer - same as main landing */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-section">
            <div className="footer-logo">
              {logoUrl ? (
                <img src={getImageUrl(logoUrl) || logoUrl} alt="Syllera" className="landing-logo-img" />
              ) : (
                <span className="material-icons">school</span>
              )}
              <span>Syllera</span>
            </div>
            <p className="footer-description">
              {t('footer.tagline')}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t('footer.product')}</h4>
            <ul>
              <li><a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>{t('nav.features')}</a></li>
              <li><a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>{t('nav.pricing')}</a></li>
              <li><a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>{t('nav.howItWorks')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.company')}</h4>
            <ul>
              <li><a href="/about">{t('footer.about')}</a></li>
              <li><a href="/contact">{t('footer.contact')}</a></li>
              <li><a href="/blog">{t('footer.blog')}</a></li>
              <li><a href="/partners">{t('footer.becomeAPartner')}</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.support')}</h4>
            <ul>
              <li><a href="/#faq" onClick={(e) => { e.preventDefault(); navigate('/#faq'); }}>{t('footer.faq')}</a></li>
              <li><a href="/help-center">{t('footer.helpCenter')}</a></li>
              <li><a href="/contact-support">{t('footer.contactSupport')}</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
          <div className="footer-links">
            <a href="/privacy-policy">{t('footer.privacyPolicy')}</a>
            <a href="/terms-of-service">{t('footer.termsOfService')}</a>
            <a href="/cookie-policy">{t('footer.cookiePolicy')}</a>
            <a href="/refund-policy">{t('footer.refundPolicy')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnersPage;

